import { useActivity } from '@/hooks/api';
import { StorageService, useBrowserTab } from '@/hooks/useBrowserTab';
import { useInactivityTimeout } from '@/hooks/useInactivityTimeout';
import { routerPaths } from '@ax/constants';
import { formatEpochToDate } from '@/utils';
import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useAppSelector,
  selectNextPasswordResetInstantEpochInSeconds,
  selectProviderUserSettings,
} from '@ax/redux-store';

interface SecurityProviderProps {
  children?: ReactNode;
}

const localStorageService: StorageService = {
  get: (key) => localStorage.getItem(key),
  set: (key, value) => localStorage.setItem(key, value),
};

const SecurityProvider = ({ children }: SecurityProviderProps) => {
  const navigate = useNavigate();
  const passwordResetDateEpoch = useAppSelector(selectNextPasswordResetInstantEpochInSeconds);
  const providerUserSettings = useAppSelector(selectProviderUserSettings);
  const { isTimedOut } = useInactivityTimeout(
    providerUserSettings?.inactivityTimeoutInMinutes ?? 15,
  );
  const browserTab = useBrowserTab(localStorageService);
  const passwordResetDate = passwordResetDateEpoch
    ? formatEpochToDate(passwordResetDateEpoch)
    : null;
  const { mutateAsync: activity } = useActivity();

  useEffect(() => {
    if (passwordResetDate) {
      const currentDate = new Date();
      const resetDate = new Date(passwordResetDate);

      if (resetDate <= currentDate) {
        navigate(routerPaths.resetPassword);
      }
    }

    if (isTimedOut) {
      navigate(`${routerPaths.login}?expired=yes`);
    }

    const lastActivity = async () => {
      await activity();
    };

    const interval = setInterval(lastActivity, 30000);

    return () => clearInterval(interval);
  }, [passwordResetDate, browserTab.isAlreadyOpenInAnotherTab, isTimedOut, navigate]);

  return <>{children}</>;
};

export default SecurityProvider;
